/**
 * Contract for the in-tree endpoint perf audit.
 *
 *   GET  /api/bench/manifest  → the static list of endpoints we know how to
 *                              benchmark (mirror of scripts/perf/manifest.mjs)
 *   POST /api/bench/run       → run the benchmark against the live local
 *                              server and return the per-endpoint timings
 *   GET  /api/bench/history   → previous runs persisted to docs/perf/history/
 *
 * Imported by:
 *   - excellent/app/api/bench/(manifest|run|history)/route.ts (server)
 *   - excellent/app/settings/advanced/performance/_client.tsx (client)
 */
import { z } from "zod";
import type { Operation } from "../types";

export const perfEndpointSchema = z.object({
  name: z.string(),
  url: z.string(),
  /** Display group derived from the leading segment of name. */
  group: z.string(),
});
export type PerfEndpoint = z.infer<typeof perfEndpointSchema>;

export const perfManifestResponse = z.object({
  endpoints: z.array(perfEndpointSchema),
  groups: z.array(z.string()),
  placeholders: z.record(z.string(), z.union([z.string(), z.number()])),
});
export type PerfManifestResponse = z.infer<typeof perfManifestResponse>;

export const perfResultSchema = z.object({
  name: z.string(),
  url: z.string(),
  status: z.number(),
  ok_rate: z.number(),
  bytes: z.number(),
  etag: z.string().nullable().optional(),
  min: z.number(),
  avg: z.number(),
  p50: z.number(),
  p95: z.number(),
  max: z.number(),
  samples: z.number(),
  error: z.string().optional(),
});
export type PerfResult = z.infer<typeof perfResultSchema>;

export const perfRunResponse = z.object({
  started_at: z.string(),
  finished_at: z.string(),
  base_url: z.string(),
  iterations: z.number(),
  warmup: z.number(),
  total: z.number(),
  ok: z.number(),
  overall_p50_ms: z.number(),
  overall_p95_ms: z.number(),
  use_etag: z.boolean(),
  results: z.array(perfResultSchema),
});
export type PerfRunResponse = z.infer<typeof perfRunResponse>;

export const perfRunBody = z.object({
  /** Substring filter against endpoint name / url. */
  filter: z.string().optional(),
  /** Iterations per endpoint after warmup. Capped server-side to keep the
   *  audit interactive — heavy values are for the CLI harness. */
  iterations: z.number().int().min(1).max(50).optional(),
  warmup: z.number().int().min(0).max(20).optional(),
  /** When true, the second+ iterations send the warmup's ETag header so we
   *  measure the conditional-GET fast-path. */
  use_etag: z.boolean().optional(),
});

export const perfHistoryEntry = z.object({
  file: z.string(),
  started_at: z.string(),
  overall_p50_ms: z.number(),
  overall_p95_ms: z.number(),
  ok: z.number(),
  total: z.number(),
  use_etag: z.boolean(),
});
export type PerfHistoryEntry = z.infer<typeof perfHistoryEntry>;

export const perfHistoryResponse = z.object({
  runs: z.array(perfHistoryEntry),
});

export const perf = {
  manifest: {
    method: "GET",
    path: () => "/api/bench/manifest",
    response: perfManifestResponse,
  } satisfies Operation<void, void, void, PerfManifestResponse>,
  run: {
    method: "POST",
    path: () => "/api/bench/run",
    body: perfRunBody,
    response: perfRunResponse,
  } satisfies Operation<void, void, z.infer<typeof perfRunBody>, PerfRunResponse>,
  history: {
    method: "GET",
    path: () => "/api/bench/history",
    response: perfHistoryResponse,
  } satisfies Operation<void, void, void, z.infer<typeof perfHistoryResponse>>,
};
